<template>
    <grid
        ref="grid"
        :column-defs="columnDefs"
        :fetch-callback="fetchCallback"
    >
        <busy-modal :show="busy" :overlay="true"></busy-modal>
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            variant="primary"
            @click="refresh"
        >Refresh</responsive-button>
         <responsive-button
            :icon="['fas', 'upload']"
            variant="primary"
            @click="showUploadModal"
        >Upload Document</responsive-button>
        <document-upload-modal-form
            :modal-id="uploadModalID"
            :key="uploadModalKey"
            :create="createUploadedFile"
            :areas-options="areasOptions"
            :propertiesOptions="propertyOptions"
            @success-save="handleAddDocumentSuccess"
        ></document-upload-modal-form>
    </grid>
</template>

<script>
import API from '../../../api';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import {
    DOCUMENT_UPLOADED_FILES_ENDPOINT,
    DOCUMENT_UPLOADED_FILE_BY_ID_ENDPOINT,
    DOCUMENT_UPLOADED_FILE_RENTAL_PROPERTY_OPTIONS_ENDPOINT,
    DOCUMENT_UPLOADED_FILE_RENTAL_PROPERTY_AREAS_OPTIONS_ENDPOINT,
    DOCUMENT_UPLOADED_FILE_GRID_ENDPOINT,
} from '../../../endpoints';
import Grid from '../../generic/grid/grid.vue';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import { escapeHTML } from '../../../utils/strings';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import DocumentUploadModalForm from './document-upload-modal-form.vue';
import { formatResponseError } from '../../../utils/api';

export default {
    name: 'DocumentUploads',
    components: {
        Grid,
        BusyModal,
        ResponsiveButton,
        DocumentUploadModalForm,
        actionsCellRenderer: ActionsCellRenderer,
    },
    data() {
        return {
            busy: false,
            documents: [],
            properties: [],
            areas: [],
            uploadModalID: 'upload-modal-id',
            uploadModalKey: 0,
            columnDefs: [
                {
                    field: 'created',
                    headerName: 'Created',
                    type: 'dateTimeColumn',
                    sort: 'desc',
                },
                {
                    field: 'filename',
                    headerName: 'Filename',
                    cellRenderer: ({ data }) =>
                            `<a target="_blank" href="${data.download_link}">${data.filename}</a>`,
                },
                {
                    field: 'description',
                    headerName: 'Description',
                },
                {
                    field: 'rental_property.name',
                    headerName: 'Rental Property',
                    cellEditor: 'selectCellEditor',
                    editable: true,
                    cellClass: 'text-primary lx-editable-cell',
                    cellEditorParams: ({ data }) => ({
                        options: () => {
                            if (data.rental_property && !this.propertyOptions.map(o=>o.value).includes(data.rental_property.id)) {
                                return [
                                    { value: data.rental_property.id, text: data.rental_property.name },
                                    ...this.propertyOptions
                                ];
                            }
                            return this.propertyOptions;
                        }
                    }),
                    valueGetter: ({ data }) => data && data.rental_property && data.rental_property.id,
                    cellRenderer: ({ data }) => escapeHTML(data && data.rental_property && data.rental_property.name || 'N/A'),
                    valueSetter: async ({ node, data, newValue }) => {
                        const oldRentalProperty = data.rental_property;
                        (async () => {
                            try {
                                await API.patch(
                                    DOCUMENT_UPLOADED_FILE_BY_ID_ENDPOINT(data.id), { rental_property: newValue }
                                );
                                await this.fetchPropertyOptions();
                            } catch (err) {
                                const { generic } = formatResponseError(error);
                                this.errorToast({ text: generic || `Could not update document.` });
                                data.rental_property = oldRentalProperty;
                                node.setData(data)
                            }
                        })();
                        const rentalProperty = this.propertyOptions.find(p => p.value === newValue);
                        data.rental_property = { id: rentalProperty.value, name: rentalProperty.text };
                        return true;
                    },
                },
                {
                    field: 'category.area',
                    headerName: 'Category',
                    cellEditor: 'selectCellEditor',
                    editable: true,
                    cellClass: 'text-primary lx-editable-cell',
                    cellEditorParams: ({ data }) => ({
                        options: () => {
                            if (data.category && !this.areasOptions.map(o=>o.value).includes(data.category.id)) {
                                return [
                                    { value: data.category.id, text: data.category.area },
                                    ...this.areasOptions
                                ];
                            }
                            return this.areasOptions;
                        }
                    }),
                    valueGetter: ({ data }) => data && data.category && data.category.id,
                    cellRenderer: ({ data }) => escapeHTML(data && data.category && data.category.area || 'N/A'),
                    valueSetter: async ({ node, data, newValue }) => {
                        const oldCategory = data.category;
                        (async () => {
                            try {
                                await API.patch(
                                    DOCUMENT_UPLOADED_FILE_BY_ID_ENDPOINT(data.id), { category: newValue }
                                );
                                await this.fetchAreasOptions();
                            } catch (err) {
                                const { generic } = formatResponseError(error);
                                this.errorToast({ text: generic || `Could not update document.` });
                                data.category = oldCategory;
                                node.setData(data)
                            }
                        })();
                        const category = this.areasOptions.find(p => p.value === newValue);
                        data.category = { id: category.value, area: category.text };
                        return true;
                    },
                },
                {
                    headerName: '',
                    maxWidth: 80,
                    minWidth: 80,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        delete: async (data) => {
                            try {
                                this.busy = true;
                                await API.delete(DOCUMENT_UPLOADED_FILE_BY_ID_ENDPOINT(data.id));
                                this.busy = false;
                            } catch (error) {
                                const { generic } = formatResponseError(error);
                                this.errorToast({ text: generic || `Could not delete document.` });
                                this.busy = false;
                                return;
                            }

                            this.refresh();
                            this.fetchAreasOptions();
                            this.fetchPropertyOptions();
                        },
                    }
                },
            ],
        }
    },
    computed: {
        propertyOptions() {
            return [
                { value: null, text: 'N/A' },
                ...this.properties.map(e => ({
                    value: e.id,
                    text: e.name,
                }))
            ];
        },
        areasOptions() {
            return [
                { value: null, text: 'N/A' },
                ...this.areas.map(e => ({
                    value: e.id,
                    text: e.area,
                }))
            ];
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(DOCUMENT_UPLOADED_FILE_GRID_ENDPOINT(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                    },
                });
            };
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        async handleAddDocumentSuccess() {
            this.successToast({ text: 'Document successfully uploaded.' });
            await this.load();
            this.refresh();
        },
        showUploadModal() {
            this.uploadModalKey += 1;
            this.$nextTick(() => {
                this.$bvModal.show(this.uploadModalID);
            })
        },
        createUploadedFile(payload) {
            return API.post(DOCUMENT_UPLOADED_FILES_ENDPOINT(), payload)
        },
        async load() {
            try {
                this.busy = true;
                await this.fetchPropertyOptions();
                await this.fetchAreasOptions();
            } finally {
                this.busy = false;
            }
        },
        async fetchPropertyOptions() {
            try {
                this.busy = true;
                const response = await API.get(DOCUMENT_UPLOADED_FILE_RENTAL_PROPERTY_OPTIONS_ENDPOINT());
                this.properties = response.data
            } catch (err) {
                const { generic } = formatResponseError(error);
                this.errorToast({ text: generic || `Could not fetch property options.` });
            } finally {
                this.busy = false;
            }
        },
        async fetchAreasOptions() {
            try {
                this.busy = true;
                const response = await API.get(DOCUMENT_UPLOADED_FILE_RENTAL_PROPERTY_AREAS_OPTIONS_ENDPOINT());
                this.areas = response.data
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.errorToast({ text: generic || `Could not fetch category options.` });
            } finally {
                this.busy = false;
            }
        },
    }
}
</script>